/**
 * A chart's data colours (lifted 2026-11-03 for the Sales Funnel page, docs/PLAN-sales-funnel-20261103.md, from
 * src/components/Activity/activityTheme.ts, which re-exports the first three unchanged). This module imports nothing.
 *
 * CATEGORICAL_SERIES: the validated reference palette (dataviz method; six-checks validator run 2026-08-03: lightness
 * band, chroma floor, CVD separation and the normal-vision floor all PASS on the light surface; the contrast WARN's
 * relief obligation is met by the legend and a table of the same figures). Assigned in FIXED order and never cycled;
 * the overflow bucket ("other") gets the neutral.
 *
 * CATEGORICAL_SERIES_DUSK: the same six hues stepped for the dusk well (#161616), in the same order. Run 2026-11-03
 * (`validate_palette.js --mode dark --surface "#161616"`): the noon steps FAIL the dark lightness band there (orange,
 * yellow, magenta too light, violet too dark at 2.1:1), and these PASS every check — band, chroma, CVD separation
 * (worst adjacent ΔE 8.4), the normal-vision floor (19.3) and 3:1 on the well. The noon set re-checked on the noon well
 * (#F1EEE7) passes with the contrast WARN, so a stack always carries its legend and its table.
 *
 * GOLD_ORDINAL_NOON / _DUSK: one gold hue (the accent's, OKLCH h 87°, C 0.10) in six lightness steps for ordered marks
 * (a funnel's steps), faint to strong: by day light to dark, at dusk dark to light. Run 2026-11-03 with `--ordinal`:
 * monotone, every adjacent ΔL ≥ 0.06, one hue, the faint end 2.11:1 on the noon well and 2.64:1 on the dusk well.
 */

export const CATEGORICAL_SERIES = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#4a3aa7', // violet
] as const;

export const CATEGORICAL_SERIES_DUSK = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#9085e9', // violet
] as const;

/** The neutral for "other" (3.0:1 on the noon well, 5.2:1 on the dusk well). */
export const OTHER_SERIES_COLOR = '#8a8a83';

export const GOLD_ORDINAL_NOON = ['#c1a258', '#ad8e44', '#997b30', '#866818', '#735600', '#604400'] as const;
export const GOLD_ORDINAL_DUSK = ['#735600', '#896c1d', '#a08237', '#b8994f', '#d0b066', '#e8c87e'] as const;

/** The ramp's step for the i-th of n ordered marks, spread so the first is the faintest and the last the strongest. */
export function ordinalStep(i: number, n: number, steps = GOLD_ORDINAL_NOON.length): number {
  if (n <= 1) return steps - 1;
  return Math.round((Math.max(0, Math.min(n - 1, i)) * (steps - 1)) / (n - 1));
}

/**
 * Mix a hex color toward white (t > 0) or black (t < 0), t in [-1, 1].
 * Used to give same-family systems distinct shades of their family color —
 * color follows the FAMILY (matching the provider dots on the events table
 * and the ⓘ popup), shade separates siblings.
 */
export function mixHex(hex: string, t: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return hex;
  const target = t >= 0 ? 255 : 0;
  const k = Math.min(1, Math.abs(t));
  const mix = (c: number) => Math.round(c + (target - c) * k);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}
